import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { weiToMoani, formatMoani } from '../utils/moaniUtils';
import MoaniDisplay from './MoaniDisplay';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  CardContent,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  Fingerprint,
  Person,
  Block,
  Report,
  AccountBalanceWallet,
  TravelExplore,
  Style,
  Badge,
  AccessTime,
  VerifiedUser,
  Delete as DeleteIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  Category as CategoryIcon,
  FilterList as FilterListIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import api from '../api';
import axios from 'axios';
import { useUser } from '../UserContext';
import { useAuth } from '../AuthContext';
import UserDeletionDialog from './UserDeletionDialog';
import NftRefreshDialog, { REFRESH_TIMEOUT_MS } from './NftRefreshDialog';
import { extractApiError } from '../api';

function UserDetails({ userId }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addToRecentUsers, selectUserById } = useUser();
  const { user: currentUser } = useAuth();
  
  // States for linked account management
  const [alerts, setAlerts] = useState([]);
  
  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };
  
  // Fetch user data
  const { data: selectedUser, isLoading: userLoading } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      const userData = response.data;
      // Add to recent users when data is fetched
      addToRecentUsers(userData);
      // Set notes from user data
      setNotes(userData.notes || '');
      return userData;
    },
    enabled: !!userId
  });

  const [saveSuccess] = useState(false);
  const [openToken, setOpenToken] = useState(null);
  const [toggleLoading, setToggleLoading] = useState({});
  const [moaniDialogOpen, setMoaniDialogOpen] = useState(false);
  const [newMoaniBalance, setNewMoaniBalance] = useState('');
  const [moaniDescription, setMoaniDescription] = useState('');
  const [updatingMoani, setUpdatingMoani] = useState(false);
  const [moaniUpdateSuccess, setMoaniUpdateSuccess] = useState(false);
  const [paradisePointsDialogOpen, setParadisePointsDialogOpen] = useState(false);
  const [newParadisePoints, setNewParadisePoints] = useState('');
  const [updatingParadisePoints, setUpdatingParadisePoints] = useState(false);
  const [paradisePointsUpdateSuccess, setParadisePointsUpdateSuccess] = useState(false);
  const [notes, setNotes] = useState('');
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSuccess, setNotesSuccess] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [refreshingNFTs, setRefreshingNFTs] = useState(false);
  const [nftRefreshOpen, setNftRefreshOpen] = useState(false);
  const [nftRefreshPhase, setNftRefreshPhase] = useState('idle');
  const [nftRefreshElapsed, setNftRefreshElapsed] = useState(0);
  const [nftRefreshResult, setNftRefreshResult] = useState(null);
  const [nftRefreshError, setNftRefreshError] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [selectedTraits, setSelectedTraits] = useState({}); // { traitType: [values] }
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  
  // Fetch IP-related users (moved after currentTab state declaration)
  const { data: ipRelatedData, isLoading: ipRelatedLoading, refetch: refetchIPRelated } = useQuery({
    queryKey: ['ipRelatedUsers', userId, currentTab],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}/ip-related-users`);
      return response.data;
    },
    enabled: !!userId && (currentTab === 1) // Only fetch when IP Related Users tab is active
  });

  // Note templates for common ban reasons
  const noteTemplates = [
    { value: '', label: 'Select a template...' },
    { value: 'Permanently banned due to exploiting game mechanics and gaining unfair advantages.', label: 'Exploiting - Permanent Ban' },
    { value: 'Banned due to automatic flagging system detecting suspicious activity patterns.', label: 'Auto-flagged - Suspicious Activity' },
    { value: 'Permanently banned for using third-party cheating software or modifications.', label: 'Cheating Software - Permanent Ban' },
    { value: 'Banned for harassment and toxic behavior towards other players.', label: 'Harassment - Toxic Behavior' },
    { value: 'Permanently banned for real money trading (RMT) violations.', label: 'RMT Violations - Permanent Ban' },
    { value: 'Banned for creating multiple accounts to circumvent previous bans.', label: 'Ban Evasion - Multiple Accounts' },
    { value: 'Permanently banned for duplication exploits and item manipulation.', label: 'Duplication Exploits - Permanent Ban' },
    { value: 'Banned for botting and automated gameplay violations.', label: 'Botting - Automated Gameplay' },
    { value: 'Permanently banned for severe Terms of Service violations.', label: 'ToS Violations - Permanent Ban' },
    { value: 'Banned pending investigation into reported activities.', label: 'Under Investigation - Temporary' }
  ];

  const handleTemplateSelect = (templateValue) => {
    if (templateValue) {
      const timestamp = new Date().toLocaleString();
      const templateNote = `[${timestamp}] ${templateValue}`;
      
      if (notes.trim()) {
        setNotes(notes + '\n\n' + templateNote);
      } else {
        setNotes(templateNote);
      }
    }
    setSelectedTemplate('');
  };

  const handleDeleteSuccess = () => {
    showAlert('User account soft-deleted successfully - auth data cleared, game data preserved', 'success');
    
    // Refresh user data to show updated info
    queryClient.invalidateQueries(['user', userId]);
  };

  // Fetch linked accounts
  const { data: linkedAccounts = [], isLoading: loadingLinked, error: linkedError } = useQuery({
    queryKey: ['linkedAccounts', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser?.id) return [];
      console.log('Fetching linked accounts for user:', selectedUser.id);
      try {
        const res = await api.get(`/users/${selectedUser.id}/linked-accounts`);
        console.log('Linked accounts response:', res.data);
        return res.data;
      } catch (err) {
        console.error('Error fetching linked accounts:', err);
        if (err.response && err.response.status === 404) {
          console.log('Linked accounts endpoint not found, returning empty array');
          return [];
        }
        throw err;
      }
    },
    enabled: !!selectedUser?.id,
    retry: 1,
    staleTime: 60000,
    onError: (error) => {
      console.error('Error in linked accounts query:', error);
    }
  });

  // Remove linked account mutation
  const removeLinkedAccountMutation = useMutation({
    mutationFn: async ({ userId, accountId }) => {
      const response = await api.delete(`/users/${userId}/linked-accounts/${accountId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['linkedAccounts', selectedUser?.id]);
      showAlert('Linked account removed successfully', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to remove linked account', 'error');
    }
  });

  // Toggle linked account status mutation
  const toggleLinkedAccountMutation = useMutation({
    mutationFn: async ({ userId, accountId, isActive }) => {
      const response = await api.put(`/users/${userId}/linked-accounts/${accountId}`, { isActive });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['linkedAccounts', selectedUser?.id]);
      showAlert(`Linked account ${data.status === 'ACTIVE' ? 'enabled' : 'disabled'} successfully`, 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to update linked account status', 'error');
    }
  });

  const handleRemoveLinkedAccount = (accountId) => {
    if (window.confirm('Are you sure you want to remove this linked account? This action cannot be undone.')) {
      removeLinkedAccountMutation.mutate({ userId: selectedUser.id, accountId });
    }
  };

  const handleToggleLinkedAccount = (accountId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'activate' : 'deactivate';
    if (window.confirm(`Are you sure you want to ${action} this linked account?`)) {
      toggleLinkedAccountMutation.mutate({ 
        userId: selectedUser.id, 
        accountId, 
        isActive: newStatus 
      });
    }
  };

  const prettyPrint = (value) => {
    if (value === null || value === undefined) {
      return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Not set</Typography>;
    }
    if (value === '') {
      return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Empty</Typography>;
    }
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(parsed, null, 2)}</Typography>;
      } catch {
        return value;
      }
    }
    if (typeof value === 'object' && value !== null) {
      return <Typography variant="body2" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{JSON.stringify(value, null, 2)}</Typography>;
    }
    return String(value);
  };

  // Helper function to get Paradise Server API URL
  const getGameApiUrl = () => {
    // Check environment from localStorage (matches PTAdmin's environment switcher)
    const env = localStorage.getItem('selectedEnvironment') || 'production';
    return env === 'development' 
      ? 'https://dev-api.paradisetycoon.com'
      : 'https://beta-api.paradisetycoon.com';
  };

  // Fetch contracts from Paradise Server
  const { data: contracts = [], isLoading: contractsLoading } = useQuery({
    queryKey: ['contracts', getGameApiUrl()],
    queryFn: async () => {
      const gameApiUrl = getGameApiUrl();
      const response = await axios.get(`${gameApiUrl}/general/getContracts`, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });
      // Paradise Server returns { completedSuccessfully: true, data: [...] }
      return response.data?.data || [];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    retry: 1
  });

  // Create a map of contract addresses to contract info for quick lookup
  const contractMap = useMemo(() => {
    const map = new Map();
    contracts.forEach(contract => {
      map.set(contract.address.toLowerCase(), contract);
    });
    return map;
  }, [contracts]);

  // Parse owned tokens
  const ownedTokens = useMemo(() => {
    if (!selectedUser?.ownedTokens) return [];
    try {
      return Array.isArray(selectedUser.ownedTokens)
        ? selectedUser.ownedTokens
        : JSON.parse(selectedUser.ownedTokens);
    } catch {
      return [];
    }
  }, [selectedUser?.ownedTokens]);

  // Extract collections dynamically using contract data
  const collections = useMemo(() => {
    const collectionMap = new Map();
    ownedTokens.forEach(token => {
      const contractAddress = token.contract_address?.toLowerCase() || 'unknown';
      if (!collectionMap.has(contractAddress)) {
        // Try to get contract info from Paradise Server data
        const contractInfo = contractMap.get(contractAddress);
        let collectionName;
        let chainName;
        
        if (contractInfo) {
          // Use contract name and chain from server
          collectionName = contractInfo.name;
          chainName = contractInfo.chainName;
        } else {
          // Fallback: try to get a readable name from token data
          collectionName = token.external_url || token.external_link || contractAddress;
          // If it's a known contract pattern, use a better name
          if (contractAddress.includes('paradise') || contractAddress.includes('island')) {
            collectionName = 'Paradise Islands';
          } else if (contractAddress.includes('furniture') || contractAddress.includes('item')) {
            collectionName = 'Furniture & Items';
          } else if (contractAddress !== 'unknown') {
            // Use shortened address as fallback
            collectionName = `${contractAddress.slice(0, 6)}...${contractAddress.slice(-4)}`;
          }
        }
        
        collectionMap.set(contractAddress, {
          address: contractAddress,
          name: collectionName,
          chainName: chainName,
          count: 0
        });
      }
      collectionMap.get(contractAddress).count++;
    });
    return Array.from(collectionMap.values()).sort((a, b) => b.count - a.count);
  }, [ownedTokens, contractMap]);

  // Initialize expanded collections when collections are first loaded (expand all by default)
  useEffect(() => {
    if (collections.length > 0 && expandedCollections.size === 0) {
      const allCollectionAddresses = new Set(collections.map(c => c.address));
      setExpandedCollections(allCollectionAddresses);
    }
  }, [collections, expandedCollections.size]);

  // Extract all unique traits dynamically
  const availableTraits = useMemo(() => {
    const traitsMap = new Map();
    ownedTokens.forEach(token => {
      if (token.attributes && Array.isArray(token.attributes)) {
        token.attributes.forEach(attr => {
          if (attr.trait_type && attr.value !== null && attr.value !== undefined) {
            const traitType = attr.trait_type;
            const traitValue = String(attr.value);
            if (!traitsMap.has(traitType)) {
              traitsMap.set(traitType, new Set());
            }
            traitsMap.get(traitType).add(traitValue);
          }
        });
      }
    });
    // Convert to object with sorted arrays
    const result = {};
    traitsMap.forEach((values, traitType) => {
      result[traitType] = Array.from(values).sort();
    });
    return result;
  }, [ownedTokens]);

  // Group tokens by collection and apply filters
  const groupedAndFilteredTokens = useMemo(() => {
    let filtered = ownedTokens;

    // Filter by collection
    if (selectedCollection) {
      filtered = filtered.filter(token => {
        const contractAddress = token.contract_address?.toLowerCase() || 'unknown';
        return contractAddress === selectedCollection.toLowerCase();
      });
    }

    // Filter by traits
    Object.entries(selectedTraits).forEach(([traitType, selectedValues]) => {
      if (selectedValues && selectedValues.length > 0) {
        filtered = filtered.filter(token => {
          if (!token.attributes || !Array.isArray(token.attributes)) return false;
          const tokenAttr = token.attributes.find(attr => attr.trait_type === traitType);
          if (!tokenAttr) return false;
          return selectedValues.includes(String(tokenAttr.value));
        });
      }
    });

    // Group by collection
    const grouped = new Map();
    filtered.forEach(token => {
      const contractAddress = token.contract_address?.toLowerCase() || 'unknown';
      const collection = collections.find(c => c.address === contractAddress);
      const collectionKey = collection?.address || 'unknown';
      if (!grouped.has(collectionKey)) {
        grouped.set(collectionKey, {
          collection: collection || { address: 'unknown', name: 'Unknown Collection', count: 0 },
          tokens: []
        });
      }
      grouped.get(collectionKey).tokens.push(token);
    });

    return Array.from(grouped.values()).sort((a, b) => 
      b.collection.count - a.collection.count
    );
  }, [ownedTokens, selectedCollection, selectedTraits, collections]);

  const handleCollectionChange = (event) => {
    setSelectedCollection(event.target.value);
  };

  const handleTraitChange = (traitType, values) => {
    setSelectedTraits(prev => ({
      ...prev,
      [traitType]: values
    }));
  };

  const clearFilters = () => {
    setSelectedCollection('');
    setSelectedTraits({});
  };

  const toggleCollectionExpanded = (collectionAddress) => {
    setExpandedCollections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(collectionAddress)) {
        newSet.delete(collectionAddress);
      } else {
        newSet.add(collectionAddress);
      }
      return newSet;
    });
  };

  const handleToggle = async (field) => {
    setToggleLoading((prev) => ({ ...prev, [field]: true }));
    try {
      let updateData = {};
      
      if (field === 'banned') {
        // Ban button toggles both banned and cheater flags
        const newBannedState = !selectedUser.banned;
        updateData = { 
          banned: newBannedState,
          cheater: newBannedState // Set cheater to same value as banned
        };
        // Set bannedAt when banning, clear it when unbanning
        if (newBannedState) {
          updateData.bannedAt = new Date().toISOString();
        } else {
          updateData.bannedAt = null;
        }
      } else if (field === 'permaban') {
        // Permaban button
        const newPermabanState = !selectedUser.permaban;
        updateData = { [field]: newPermabanState };
        
        // Add automatic note for permaban
        if (newPermabanState) {
          const timestamp = new Date().toLocaleString();
          const permabanNote = `[${timestamp}] User permanently banned by admin.`;
          
          const currentNotes = selectedUser.notes || '';
          const newNotes = currentNotes.trim() 
            ? currentNotes + '\n\n' + permabanNote
            : permabanNote;
          
          updateData.notes = newNotes;
          setNotes(newNotes); // Update local state
        }
      } else {
        // Other toggles work normally
        updateData = { [field]: !selectedUser[field] };
      }
      
      await api.put(`/users/${selectedUser.id}`, updateData);
      queryClient.invalidateQueries(['user', userId]);
    } catch { }
    setToggleLoading((prev) => ({ ...prev, [field]: false }));
  };

  const handleSaveNotes = async () => {
    setNotesLoading(true);
    try {
      await api.put(`/users/${selectedUser.id}`, { notes });
      queryClient.invalidateQueries(['user', userId]);
      setNotesSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setNotesSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes: ' + (error.response?.data?.error || error.message));
    } finally {
      setNotesLoading(false);
    }
  };

  // Ban related user
  const banRelatedUser = useMutation({
    mutationFn: async ({ userId: targetUserId, sharedIPs }) => {
      const ipList = sharedIPs.map(s => s.ip).join(', ');
      const reason = `Investigating multiaccounting and related users. Shared IPs: ${ipList}`;
      
      // Get current notes
      const userDataResponse = await api.get(`/users/${targetUserId}`);
      const currentNotes = userDataResponse.data.notes || '';
      const timestamp = new Date().toLocaleString();
      const banNote = `[${timestamp}] ${reason}`;
      const newNotes = currentNotes.trim() 
        ? currentNotes + '\n\n' + banNote
        : banNote;
      
      await api.put(`/users/${targetUserId}`, {
        banned: true,
        cheater: true,
        notes: newNotes,
        bannedAt: new Date().toISOString()
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ipRelatedUsers', userId]);
      queryClient.invalidateQueries(['user', userId]);
      setBanDialogOpen(false);
      setUserToBan(null);
      showAlert('User banned successfully', 'success');
    },
    onError: (error) => {
      showAlert('Failed to ban user: ' + (error.response?.data?.error || error.message), 'error');
    }
  });

  const handleBanClick = (relatedUser) => {
    setUserToBan(relatedUser);
    setBanDialogOpen(true);
  };

  const handleBanConfirm = () => {
    if (userToBan) {
      banRelatedUser.mutate({
        userId: userToBan.userId,
        sharedIPs: userToBan.sharedIPs
      });
    }
  };

  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    if (nftRefreshPhase !== 'running') return undefined;
    const started = Date.now();
    const id = setInterval(() => {
      setNftRefreshElapsed(Date.now() - started);
    }, 200);
    return () => clearInterval(id);
  }, [nftRefreshPhase]);

  const handleRefreshNFTs = async () => {
    setNftRefreshOpen(true);
    setNftRefreshPhase('running');
    setNftRefreshElapsed(0);
    setNftRefreshResult(null);
    setNftRefreshError(null);
    setRefreshingNFTs(true);

    try {
      const response = await api.post(
        `/users/${userId}/refresh-nft-collection`,
        {},
        { timeout: REFRESH_TIMEOUT_MS },
      );
      setNftRefreshResult(response.data);
      setNftRefreshPhase('success');
      queryClient.invalidateQueries(['user', userId]);
    } catch (err) {
      const data = err.response?.data;
      setNftRefreshError({
        message: extractApiError(err, 'NFT refresh failed'),
        isTimeout: err.code === 'ECONNABORTED' || data?.isTimeout,
        gameApiUrl: data?.gameApiUrl,
        previous: data?.previous,
        walletAddresses: data?.walletAddresses,
      });
      setNftRefreshPhase('error');
    } finally {
      setRefreshingNFTs(false);
    }
  };

  const handleCloseNftRefresh = () => {
    if (nftRefreshPhase === 'running') return;
    setNftRefreshOpen(false);
    setNftRefreshPhase('idle');
    setNftRefreshResult(null);
    setNftRefreshError(null);
  };

  const handleCopyNftRefreshDebug = async () => {
    const payload = nftRefreshResult || { error: nftRefreshError };
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  };

  const handleRefreshNFTsClick = () => {
    if (
      window.confirm(
        'Refresh NFT collection for this user? Large wallets (100+ Genesis Collection) may take up to 2 minutes.',
      )
    ) {
      handleRefreshNFTs();
    }
  };

  const handleCopyOwnedTokensJson = async () => {
    try {
      const json = JSON.stringify(ownedTokens, null, 2);
      await navigator.clipboard.writeText(json);
      showAlert(`Copied ${ownedTokens.length} ownedTokens to clipboard`, 'success');
    } catch (error) {
      showAlert(`Failed to copy ownedTokens: ${error.message}`, 'error');
    }
  };

  const handleMoaniBalanceUpdate = async () => {
    if (!newMoaniBalance || newMoaniBalance.trim() === '') {
      return;
    }

    setUpdatingMoani(true);
    try {
      await api.post(`/users/${selectedUser.id}/update-moani`, {
        newMoaniBalance: parseFloat(newMoaniBalance),
        description: moaniDescription || `Admin balance adjustment to ${newMoaniBalance} MOANI`
      });

      // Refresh user data
      queryClient.invalidateQueries(['user', userId]);

      setMoaniUpdateSuccess(true);
      setMoaniDialogOpen(false);
      setNewMoaniBalance('');
      setMoaniDescription('');
      
      // Hide success message after 3 seconds
      setTimeout(() => setMoaniUpdateSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating moani balance:', error);
      alert('Failed to update moani balance: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdatingMoani(false);
    }
  };

  const openMoaniDialog = () => {
    const currentBalance = selectedUser?.moani ? weiToMoani(selectedUser.moani).toFixed(2) : '0.00';
    setNewMoaniBalance(currentBalance);
    setMoaniDescription('');
    setMoaniDialogOpen(true);
  };

  const openParadisePointsDialog = () => {
    const currentPoints = selectedUser?.points != null ? String(selectedUser.points) : '0';
    setNewParadisePoints(currentPoints);
    setParadisePointsDialogOpen(true);
  };

  const handleParadisePointsUpdate = async () => {
    const parsed = parseInt(newParadisePoints, 10);
    if (isNaN(parsed) || parsed < 0) return;

    setUpdatingParadisePoints(true);
    try {
      await api.post(`/users/${selectedUser.id}/update-paradise-points`, {
        newParadisePoints: parsed
      });

      queryClient.invalidateQueries(['user', userId]);
      setParadisePointsUpdateSuccess(true);
      setParadisePointsDialogOpen(false);
      setNewParadisePoints('');
      setTimeout(() => setParadisePointsUpdateSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating paradise points:', error);
      alert('Failed to update paradise points: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdatingParadisePoints(false);
    }
  };

  const infoFields = [
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    {
      label: 'Wallet',
      key: 'wallet',
      render: v => v ? (
        <Box>
          <Typography variant="body2">{v}</Typography>
          <Box sx={{ mt: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              href={`https://snowtrace.io/address/${v}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mr: 1 }}
            >
              Snowtrace
            </Button>
            <Button
              size="small"
              variant="outlined"
              href={`https://explorer.immutable.com/address/${v}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Immutable
            </Button>
          </Box>
        </Box>
      ) : null
    },
    { label: 'Status', key: 'status' },
    {
      label: 'Paradise Points',
      key: 'points',
      render: v => {
        const canModifyPoints = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
        const points = v != null && v !== '' ? parseInt(v, 10).toLocaleString() : '0';
        return (
          <Box>
            <Typography variant="body2">{points}</Typography>
            {canModifyPoints && (
              <Button size="small" variant="outlined" onClick={openParadisePointsDialog} sx={{ mt: 0.5 }}>
                Edit Points
              </Button>
            )}
          </Box>
        );
      }
    },
    {
      label: 'Moani',
      key: 'moani',
      render: v => {
        // Check if current user can modify MOANI (admin and super_admin only, not managers)
        const canModifyMoani = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
        
        if (v === null || v === undefined || v === '') {
          return (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Not set</Typography>
              {canModifyMoani && (
                <Button size="small" variant="outlined" onClick={openMoaniDialog} sx={{ mt: 0.5 }}>
                  Set Balance
                </Button>
              )}
            </Box>
          );
        }
        try {
          return (
            <Box>
              <MoaniDisplay 
                amount={v} 
                isWei={true}
                variant="body2"
                showTooltip={true}
              />
              {canModifyMoani && (
                <Button size="small" variant="outlined" onClick={openMoaniDialog} sx={{ mt: 0.5 }}>
                  Edit Balance
                </Button>
              )}
            </Box>
          );
        } catch {
          return <Typography variant="body2" color="error">Invalid value</Typography>;
        }
      }
    },
    { label: 'Badges', key: 'badges' },
    {
      label: 'Created At',
      key: 'createdAt',
      render: v => {
        if (v === null || v === undefined || v === '') {
          return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Not set</Typography>;
        }
        try {
          const value = new Date(v).toLocaleString();
          return <Typography variant="body2">{value}</Typography>;
        } catch {
          return <Typography variant="body2" color="error">Invalid date</Typography>;
        }
      }
    },
    {
      label: 'Last Updated',
      key: 'updatedAt',
      render: v => {
        if (v === null || v === undefined || v === '') {
          return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Not set</Typography>;
        }
        try {
          const value = new Date(v).toLocaleString();
          return <Typography variant="body2">{value}</Typography>;
        } catch {
          return <Typography variant="body2" color="error">Invalid date</Typography>;
        }
      }
    },
    {
      label: 'Banned At',
      key: 'bannedAt',
      render: v => {
        if (v === null || v === undefined || v === '') {
          return <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>Not set</Typography>;
        }
        try {
          const value = new Date(v).toLocaleString();
          return <Typography variant="body2" color={selectedUser?.banned ? 'error' : 'inherit'}>{value}</Typography>;
        } catch {
          return <Typography variant="body2" color="error">Invalid date</Typography>;
        }
      }
    },
  ];

  if (userLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedUser) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">User not found.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="div" sx={{ mb: 3 }}>
        User Details: {selectedUser?.name || 'Unnamed User'}
      </Typography>
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          User information saved successfully!
        </Alert>
      )}
      {moaniUpdateSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Moani balance updated successfully!
        </Alert>
      )}
      {paradisePointsUpdateSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Paradise points updated successfully!
        </Alert>
      )}
      {alerts.map(alert => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}
      {notesSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Notes saved successfully!
        </Alert>
      )}
      
      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Overview" />
          <Tab label="IP Related Users" icon={<SecurityIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} rowSpacing={2} columnSpacing={2}>
          {infoFields.map(({ label, key, render }) => (
            <Grid
              key={key}
              sx={{ minWidth: 0 }}
              size={{
                xs: 12,
                sm: 6,
                md: 4
              }}>
              <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
              {render ? render(selectedUser[key]) : (
                <Typography variant="body2">
                  {prettyPrint(selectedUser[key])}
                </Typography>
              )}
            </Grid>
          ))}
        </Grid>
        <Stack direction="row" spacing={2} mt={3}>
          <Button
            variant={selectedUser.banned ? 'contained' : 'outlined'}
            color={selectedUser.banned ? 'error' : 'inherit'}
            onClick={() => handleToggle('banned')}
            disabled={toggleLoading.banned}
          >
            {selectedUser.banned ? 'Unban' : 'Soft Ban'}
          </Button>
          <Button
            variant={selectedUser.whitelisted ? 'contained' : 'outlined'}
            color={selectedUser.whitelisted ? 'success' : 'inherit'}
            onClick={() => handleToggle('whitelisted')}
            disabled={toggleLoading.whitelisted}
          >
            {selectedUser.whitelisted ? 'Remove from Whitelist' : 'Add to Whitelist'}
          </Button>
          <Button
            variant={selectedUser.permaban ? 'contained' : 'outlined'}
            color={selectedUser.permaban ? 'error' : 'inherit'}
            onClick={() => handleToggle('permaban')}
            disabled={toggleLoading.permaban}
            sx={{ 
              bgcolor: selectedUser.permaban ? 'error.dark' : 'inherit',
              '&:hover': {
                bgcolor: selectedUser.permaban ? 'error.main' : 'inherit'
              }
            }}
          >
            {selectedUser.permaban ? 'Remove Permaban' : 'Permaban'}
          </Button>
          {(currentUser?.role === 'manager' || currentUser?.role === 'admin' || currentUser?.role === 'super_admin') && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDeleteDialogOpen(true)}
              startIcon={<DeleteIcon />}
              sx={{ 
                ml: 'auto',
                borderColor: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  bgcolor: 'error.main',
                  color: 'white'
                }
              }}
            >
              Delete Account
            </Button>
          )}
        </Stack>
      
      {/* Admin Notes Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Admin Notes
        </Typography>
        
        {/* Template Selector */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Quick Templates</InputLabel>
          <Select
            value={selectedTemplate}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            label="Quick Templates"
          >
            {noteTemplates.map((template) => (
              <MenuItem key={template.label} value={template.value}>
                {template.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          fullWidth
          multiline
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add admin notes about this user..."
          variant="outlined"
          sx={{ mb: 2 }}
          helperText="Select a template above to quickly add common ban reasons with timestamps"
        />
        
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            onClick={handleSaveNotes}
            disabled={notesLoading || notes === (selectedUser?.notes || '')}
            startIcon={notesLoading ? <CircularProgress size={16} /> : null}
          >
            {notesLoading ? 'Saving...' : 'Save Notes'}
          </Button>
          
          {notes && notes !== (selectedUser?.notes || '') && (
            <Typography variant="body2" color="text.secondary">
              Unsaved changes
            </Typography>
          )}
        </Stack>
      </Paper>
      
      {/* Additional Information moved up */}
      <Typography variant="h6" gutterBottom>
        Additional Information
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2} mb={3}>
        {Object.entries(selectedUser || {})
          .filter(([key]) => key !== 'ownedTokens')
          .filter(([key]) => !infoFields.find(f => f.key === key))
          .filter(([key]) => !['passCache', 'passCacheRefreshed', 'evmWallet', 'tag', 'age', 'ip'].includes(key))
          .filter(([key, value]) => value !== null && value !== undefined && value !== '')
          .map(([key, value]) => {
            let Icon = null;
            switch (key) {
              case 'id': Icon = Fingerprint; break;
              case 'playerid': Icon = Person; break;
              case 'banned': Icon = Block; break;
              case 'cheater': Icon = Report; break;
              case 'gameWallet':
              case 'wallet': Icon = AccountBalanceWallet; break;
              case 'activeIsland': Icon = TravelExplore; break;
              case 'outfitHash': Icon = Style; break;
              case 'passportId': Icon = Badge; break;
              case 'online': Icon = AccessTime; break;
              case 'whitelisted': Icon = VerifiedUser; break;
              default: break;
            }
            return (
              <Grid
                key={key}
                sx={{ minWidth: 0 }}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4,
                  lg: 3
                }}>
                <Paper elevation={2} sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 2, height: '100%' }}>
                  {Icon && <Icon sx={{ color: 'primary.light', mt: 0.5 }} />}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>{key}</Typography>
                    {prettyPrint(value)}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
      </Grid>
      {/* Linked Accounts section */}
      <Typography variant="h6" gutterBottom>Linked Accounts</Typography>
      <Divider sx={{ mb: 2 }} />
      {loadingLinked ? (
        <Box py={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary" ml={2} display="inline">
            Loading linked accounts...
          </Typography>
        </Box>
      ) : linkedError ? (
        <Box py={2}>
          <Typography color="error">Error loading linked accounts: {linkedError.message}</Typography>
        </Box>
      ) : linkedAccounts.length === 0 ? (
        <Typography color="text.secondary" mb={2}>No linked accounts</Typography>
      ) : (
        <Grid container spacing={2} mb={3}>
          {linkedAccounts.map(acc => (
            <Grid
              key={acc.id}
              sx={{ minWidth: 0 }}
              size={{
                xs: 12,
                sm: 6,
                lg: 4
              }}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.light">
                      {acc.provider || acc.type || 'Linked Account'}
                    </Typography>
                    {acc.status && (
                      <Chip 
                        label={acc.status === 'ACTIVE' ? 'Active' : 'Disabled'} 
                        size="small" 
                        color={acc.status === 'ACTIVE' ? 'success' : 'error'}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                  {['manager', 'admin', 'super_admin'].includes(currentUser?.role) && (
                    <Box display="flex" gap={0.5}>
                      <Tooltip title={acc.status === 'ACTIVE' ? 'Disable Account' : 'Enable Account'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleLinkedAccount(acc.id, acc.status === 'ACTIVE')}
                          disabled={toggleLinkedAccountMutation.isLoading}
                          color={acc.status === 'ACTIVE' ? 'success' : 'warning'}
                        >
                          {acc.status === 'ACTIVE' ? <ToggleOnIcon /> : <ToggleOffIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove Account">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveLinkedAccount(acc.id)}
                          disabled={removeLinkedAccountMutation.isLoading}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                <Grid container spacing={1} rowSpacing={1} columnSpacing={1} sx={{ mb: 1 }}>
                  {Object.entries(acc).map(([key, value]) => {
                    if (value === null || value === undefined) return null;
                    if (key === 'id' || key === 'userId') return null;
                    let displayValue = value;
                    if (key === 'createdAt' || key === 'updatedAt') {
                      try {
                        displayValue = new Date(value).toLocaleString();
                      } catch (e) {
                        displayValue = value;
                      }
                    }
                    return (
                      <Grid key={key} sx={{ minWidth: 0 }} size={12}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                          <Typography variant="caption" color="text.secondary" sx={{
                            width: '40%',
                            flexShrink: 0,
                            textTransform: 'capitalize',
                            fontWeight: 500
                          }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {displayValue.toString()}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  ID: {acc.id}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Owned Tokens {ownedTokens.length > 0 && `(${ownedTokens.length})`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={ownedTokens.length === 0 ? 'No ownedTokens to copy' : 'Copy ownedTokens JSON to clipboard'}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyOwnedTokensJson}
                disabled={ownedTokens.length === 0}
              >
                Copy JSON
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={refreshingNFTs ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefreshNFTsClick}
            disabled={refreshingNFTs}
          >
            {refreshingNFTs ? 'Refreshing...' : 'Refresh NFTs'}
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />
      
      {/* Filters */}
      {ownedTokens.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle2" sx={{ mr: 1 }}>Filters:</Typography>
            
            {/* Collection Filter */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Collection</InputLabel>
              <Select
                value={selectedCollection}
                onChange={handleCollectionChange}
                label="Collection"
              >
                <MenuItem value="">All Collections</MenuItem>
                {collections.map(collection => (
                  <MenuItem key={collection.address} value={collection.address}>
                    <Box>
                      <Typography variant="body2">{collection.name}</Typography>
                      {collection.chainName && (
                        <Typography variant="caption" color="text.secondary">
                          {collection.chainName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({collection.count})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Trait Filters */}
            {Object.keys(availableTraits).length > 0 && (
              <>
                {Object.entries(availableTraits).map(([traitType, values]) => (
                  <FormControl key={traitType} size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{traitType}</InputLabel>
                    <Select
                      multiple
                      value={selectedTraits[traitType] || []}
                      onChange={(e) => handleTraitChange(traitType, e.target.value)}
                      input={<OutlinedInput label={traitType} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {values.map((value) => (
                        <MenuItem key={value} value={value}>
                          <Checkbox checked={(selectedTraits[traitType] || []).includes(value)} />
                          <ListItemText primary={value} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </>
            )}

            {/* Clear Filters Button */}
            {(selectedCollection || Object.values(selectedTraits).some(v => v && v.length > 0)) && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                variant="outlined"
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {(selectedCollection || Object.values(selectedTraits).some(v => v && v.length > 0)) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">Active filters:</Typography>
              {selectedCollection && (
                <Chip
                  label={`Collection: ${collections.find(c => c.address === selectedCollection)?.name || selectedCollection}`}
                  onDelete={() => setSelectedCollection('')}
                  size="small"
                  color="primary"
                />
              )}
              {Object.entries(selectedTraits).map(([traitType, values]) => {
                if (!values || values.length === 0) return null;
                return values.map(value => (
                  <Chip
                    key={`${traitType}-${value}`}
                    label={`${traitType}: ${value}`}
                    onDelete={() => {
                      const newValues = (selectedTraits[traitType] || []).filter(v => v !== value);
                      handleTraitChange(traitType, newValues);
                    }}
                    size="small"
                    color="secondary"
                  />
                ));
              })}
            </Box>
          )}
        </Paper>
      )}

      {/* Grouped Tokens by Collection */}
      {ownedTokens.length === 0 ? (
        <Grid sx={{ minWidth: 0 }} size={12}>
          <Typography color="text.secondary">No tokens</Typography>
        </Grid>
      ) : groupedAndFilteredTokens.length === 0 ? (
        <Grid sx={{ minWidth: 0 }} size={12}>
          <Alert severity="info">No tokens match the selected filters.</Alert>
        </Grid>
      ) : (
        groupedAndFilteredTokens.map(({ collection, tokens }) => {
          const isExpanded = expandedCollections.has(collection.address);
          
          return (
            <Paper key={collection.address} sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: 1
                }}
                onClick={() => toggleCollectionExpanded(collection.address)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <CategoryIcon color="primary" />
                  <Box>
                    <Typography variant="h6">
                      {collection.name}
                    </Typography>
                    {collection.chainName && (
                      <Typography variant="caption" color="text.secondary">
                        {collection.chainName}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${tokens.length} token${tokens.length !== 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontFamily: 'monospace' }}>
                    {collection.address.slice(0, 6)}...{collection.address.slice(-4)}
                  </Typography>
                </Box>
                <IconButton size="small">
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              
              <Collapse in={isExpanded}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {tokens.map((token, idx) => (
                    <Grid
                      key={token.token_id || idx}
                      sx={{ minWidth: 0 }}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 2
                      }}
                    >
                      <Card
                        sx={{
                          cursor: 'pointer',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4
                          }
                        }}
                        onClick={() => setOpenToken(token)}
                      >
                        {token.image && (
                          <Box sx={{ position: 'relative', width: '100%', pt: '100%' }}>
                            <CardMedia
                              component="img"
                              image={token.image}
                              alt={token.name}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          </Box>
                        )}
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" noWrap>
                            {token.name || 'Unnamed Token'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Token ID: {token.token_id}
                          </Typography>
                          {token.balance && token.balance > 1 && (
                            <Typography variant="body2" color="text.secondary">
                              Balance: {token.balance}
                            </Typography>
                          )}
                          {token.attributes && token.attributes.length > 0 && (
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {token.attributes.slice(0, 2).map((attr, attrIdx) => (
                                <Chip
                                  key={attrIdx}
                                  label={`${attr.trait_type}: ${attr.value}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                              {token.attributes.length > 2 && (
                                <Chip
                                  label={`+${token.attributes.length - 2}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>
          );
        })
      )}
      </Paper>
      )}
      
      {/* Paradise Points Update Dialog */}
      <Dialog open={paradisePointsDialogOpen} onClose={() => setParadisePointsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Paradise Points</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current: {(selectedUser?.points != null ? parseInt(selectedUser.points, 10) : 0).toLocaleString()} points
            </Typography>
            <TextField
              fullWidth
              label="New Paradise Points"
              type="number"
              value={newParadisePoints}
              onChange={(e) => setNewParadisePoints(e.target.value)}
              placeholder="Enter new points"
              InputProps={{
                inputProps: { min: 0, step: 1 }
              }}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParadisePointsDialogOpen(false)} disabled={updatingParadisePoints}>
            Cancel
          </Button>
          <Button
            onClick={handleParadisePointsUpdate}
            variant="contained"
            disabled={updatingParadisePoints || newParadisePoints === '' || parseInt(newParadisePoints, 10) < 0}
          >
            {updatingParadisePoints ? <CircularProgress size={20} /> : 'Update Points'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Moani Balance Update Dialog */}
      <Dialog open={moaniDialogOpen} onClose={() => setMoaniDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Moani Balance</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Balance: {selectedUser?.moani ? formatMoani(selectedUser.moani) : '0.00'} MOANI
            </Typography>
            
            <TextField
              fullWidth
              label="New Moani Balance"
              type="number"
              value={newMoaniBalance}
              onChange={(e) => setNewMoaniBalance(e.target.value)}
              placeholder="Enter new balance"
              InputProps={{
                inputProps: { step: "0.01", min: "0" }
              }}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Description (Optional)"
              multiline
              rows={3}
              value={moaniDescription}
              onChange={(e) => setMoaniDescription(e.target.value)}
              placeholder="Enter a description for this balance change..."
              sx={{ mb: 2 }}
            />
            
            {newMoaniBalance && selectedUser?.moani && (
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'background.default', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle2" gutterBottom>Change Summary:</Typography>
                <Typography variant="body2">
                  Current: {formatMoani(selectedUser.moani)} MOANI
                </Typography>
                <Typography variant="body2">
                  New: {parseFloat(newMoaniBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MOANI
                </Typography>
                <Typography variant="body2" color={parseFloat(newMoaniBalance || 0) - weiToMoani(selectedUser.moani) >= 0 ? 'success.main' : 'error.main'}>
                  Change: {parseFloat(newMoaniBalance || 0) - weiToMoani(selectedUser.moani) >= 0 ? '+' : ''}{(parseFloat(newMoaniBalance || 0) - weiToMoani(selectedUser.moani)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MOANI
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoaniDialogOpen(false)} disabled={updatingMoani}>
            Cancel
          </Button>
          <Button 
            onClick={handleMoaniBalanceUpdate} 
            variant="contained" 
            disabled={updatingMoani || !newMoaniBalance || newMoaniBalance.trim() === ''}
          >
            {updatingMoani ? <CircularProgress size={20} /> : 'Update Balance'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!openToken} onClose={() => setOpenToken(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{openToken?.name || 'Token Details'}</DialogTitle>
        <DialogContent>
          {openToken && (
            <Box>
              {openToken.image && (
                <Box mb={2} textAlign="center">
                  <img src={openToken.image} alt={openToken.name} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8 }} />
                </Box>
              )}
              <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 14 }}>
                {JSON.stringify(openToken, null, 2)}
              </pre>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenToken(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* IP Related Users Tab */}
      {currentTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            IP Related Users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Users who share IP addresses (exact matches or adjacent IPs) with this user.
          </Typography>

          {ipRelatedLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : ipRelatedData?.relatedUsers?.length === 0 ? (
            <Alert severity="info">
              No users found sharing IP addresses with this user.
            </Alert>
          ) : (
            <>
              {ipRelatedData?.summary && (
                <Box sx={{ mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6">{ipRelatedData.summary.totalRelatedUsers}</Typography>
                          <Typography color="text.secondary">Total Related Users</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="error.main">{ipRelatedData.summary.exactMatches}</Typography>
                          <Typography color="text.secondary">Exact IP Matches</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" color="warning.main">{ipRelatedData.summary.adjacentMatches}</Typography>
                          <Typography color="text.secondary">Adjacent IP Matches</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Shared IPs</TableCell>
                      <TableCell>Match Type</TableCell>
                      <TableCell>Relationship</TableCell>
                      <TableCell>First Seen</TableCell>
                      <TableCell>Last Seen</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ipRelatedData?.relatedUsers?.map((relatedUser) => {
                      const isExpanded = expandedUsers.has(relatedUser.userId);
                      const exactIPs = relatedUser.sharedIPs.filter(ip => ip.matchType === 'exact');
                      const adjacentIPs = relatedUser.sharedIPs.filter(ip => ip.matchType === 'adjacent');
                      
                      return (
                        <React.Fragment key={relatedUser.userId}>
                          <TableRow hover>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => {
                                  selectUserById(relatedUser.userId, navigate);
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                {relatedUser.name || relatedUser.email || relatedUser.userId.slice(0, 8)}
                              </Button>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2">
                                  {relatedUser.sharedIPCount} IP{relatedUser.sharedIPCount !== 1 ? 's' : ''}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleUserExpanded(relatedUser.userId)}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                {relatedUser.exactMatchCount > 0 && (
                                  <Chip
                                    label={`${relatedUser.exactMatchCount} Exact`}
                                    size="small"
                                    color="error"
                                  />
                                )}
                                {relatedUser.adjacentMatchCount > 0 && (
                                  <Chip
                                    label={`${relatedUser.adjacentMatchCount} Adjacent`}
                                    size="small"
                                    color="warning"
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {relatedUser.exactMatchCount > 0 ? 'Strong' : 'Weak'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {relatedUser.firstSeen ? new Date(relatedUser.firstSeen).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {relatedUser.lastSeen ? new Date(relatedUser.lastSeen).toLocaleDateString() : 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {relatedUser.banned ? (
                                <Chip label="Banned" size="small" color="error" />
                              ) : relatedUser.cheater ? (
                                <Chip label="Cheater" size="small" color="warning" />
                              ) : (
                                <Chip label="Active" size="small" color="success" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleBanClick(relatedUser)}
                                disabled={relatedUser.banned || banRelatedUser.isPending}
                              >
                                {relatedUser.banned ? 'Banned' : 'Ban'}
                              </Button>
                            </TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ py: 2, backgroundColor: 'background.default' }}>
                                <Box>
                                  {exactIPs.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" color="error.main" gutterBottom>
                                        Exact Matches ({exactIPs.length}):
                                      </Typography>
                                      <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {exactIPs.map((ipData, idx) => (
                                          <Chip
                                            key={idx}
                                            label={ipData.ip}
                                            size="small"
                                            color="error"
                                            sx={{ fontFamily: 'monospace' }}
                                          />
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                  {adjacentIPs.length > 0 && (
                                    <Box>
                                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                                        Adjacent Matches ({adjacentIPs.length}):
                                      </Typography>
                                      <Stack direction="row" spacing={1} flexWrap="wrap">
                                        {adjacentIPs.map((ipData, idx) => (
                                          <Chip
                                            key={idx}
                                            label={ipData.ip}
                                            size="small"
                                            color="warning"
                                            sx={{ fontFamily: 'monospace' }}
                                          />
                                        ))}
                                      </Stack>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {/* Ban Confirmation Dialog */}
      <Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)}>
        <DialogTitle>Ban User</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to ban <strong>{userToBan?.name || userToBan?.email || userToBan?.userId}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Reason: Investigating multiaccounting and related users
          </Typography>
          {userToBan?.sharedIPs && userToBan.sharedIPs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Shared IPs:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {userToBan.sharedIPs.map((ipData, idx) => (
                  <Chip
                    key={idx}
                    label={ipData.ip}
                    size="small"
                    color={ipData.matchType === 'exact' ? 'error' : 'warning'}
                    sx={{ fontFamily: 'monospace' }}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBanDialogOpen(false)} disabled={banRelatedUser.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleBanConfirm}
            variant="contained"
            color="error"
            disabled={banRelatedUser.isPending}
          >
            {banRelatedUser.isPending ? <CircularProgress size={20} /> : 'Ban User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <UserDeletionDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        user={selectedUser}
        onDeleteSuccess={handleDeleteSuccess}
      />

      <NftRefreshDialog
        open={nftRefreshOpen}
        phase={nftRefreshPhase}
        elapsedMs={nftRefreshElapsed}
        result={nftRefreshResult}
        error={nftRefreshError}
        userName={selectedUser?.name}
        onClose={handleCloseNftRefresh}
        onCopyDebug={handleCopyNftRefreshDebug}
      />
    </Box>
  );
}

export default UserDetails; 